// src/pages/help/HelpFAQ.tsx
// Página de Preguntas Frecuentes interactiva

import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { faqItems } from '../../data/helpCenterData';

import Navbar from '../../components/navbar/navbar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

export default function HelpFAQ() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Obtener categorías únicas
  const categories = useMemo(() => {
    const cats = new Set(faqItems.map(item => item.category));
    return ['all', ...Array.from(cats)];
  }, []);

  // Filtrar FAQs
  const filteredFAQs = useMemo(() => {
    return faqItems.filter(item => {
      const matchCategory = activeCategory === 'all' || item.category === activeCategory;
      const matchSearch = searchQuery === '' ||
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchCategory && matchSearch;
    });
  }, [activeCategory, searchQuery]);

  // Agrupar por categoría
  const groupedFAQs = useMemo(() => {
    const groups: { [key: string]: typeof faqItems } = {};
    filteredFAQs.forEach(item => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push(item);
    });
    return groups;
  }, [filteredFAQs]);

  const toggleItem = (id: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedItems(new Set(filteredFAQs.map(item => item.id)));
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  return (
    <>
      <Navbar transparent={false} />

      {/* Header */}
      <section className="py-5 bg-primary">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Centro de Ayuda', path: '/ayuda' },
              { label: 'Preguntas Frecuentes' }
            ]}
            light={true}
          />

          <div className="row align-items-center mt-4">
            <div className="col-auto">
              <span className="d-inline-flex align-items-center justify-content-center bg-white rounded-circle" style={{ width: '80px', height: '80px' }}>
                <i className="fa-solid fa-circle-question fa-2x text-primary"></i>
              </span>
            </div>
            <div className="col">
              <h1 className="text-white mb-2">Preguntas Frecuentes</h1>
              <p className="text-white-50 mb-0">Encuentra respuestas rápidas a las dudas más comunes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Búsqueda y filtros */}
      <section className="py-4 bg-light border-bottom">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-3 mb-lg-0">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <i className="fa-solid fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Buscar en las preguntas frecuentes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchQuery('')}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="col-lg-6">
              <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                {categories.map(cat => (
                  <button
                    key={cat}
                    className={`btn btn-sm ${activeCategory === cat ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cat === 'all' ? 'Todas' : cat}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lista de FAQs */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            {/* Sidebar */}
            <div className="col-lg-3 mb-4 mb-lg-0">
              <div className="card border-0 shadow-sm sticky-top" style={{ top: '100px' }}>
                <div className="card-header bg-white border-0">
                  <h6 className="mb-0">
                    <i className="fa-solid fa-layer-group me-2"></i>
                    Categorías
                  </h6>
                </div>
                <div className="card-body p-0">
                  <div className="list-group list-group-flush">
                    {categories.map(cat => {
                      const count = cat === 'all'
                        ? faqItems.length
                        : faqItems.filter(i => i.category === cat).length;
                      return (
                        <button
                          key={cat}
                          className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${activeCategory === cat ? 'active' : ''}`}
                          onClick={() => setActiveCategory(cat)}
                        >
                          <span>{cat === 'all' ? 'Todas las preguntas' : cat}</span>
                          <span className={`badge ${activeCategory === cat ? 'bg-white text-primary' : 'bg-primary'}`}>
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Acciones */}
              <div className="card border-0 shadow-sm mt-4">
                <div className="card-body">
                  <div className="d-grid gap-2">
                    <button className="btn btn-outline-primary btn-sm" onClick={expandAll}>
                      <i className="fa-solid fa-expand me-2"></i>
                      Expandir todas
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={collapseAll}>
                      <i className="fa-solid fa-compress me-2"></i>
                      Colapsar todas
                    </button>
                  </div>
                </div>
              </div>

              {/* Enlaces útiles */}
              <div className="card border-0 shadow-sm mt-4">
                <div className="card-header bg-white border-0">
                  <h6 className="mb-0">
                    <i className="fa-solid fa-link me-2"></i>
                    Enlaces útiles
                  </h6>
                </div>
                <div className="card-body p-0">
                  <div className="list-group list-group-flush">
                    <Link to="/ayuda" className="list-group-item list-group-item-action">
                      <i className="fa-solid fa-home me-2 text-muted"></i>
                      Centro de Ayuda
                    </Link>
                    <Link to="/ayuda/glosario" className="list-group-item list-group-item-action">
                      <i className="fa-solid fa-book me-2 text-muted"></i>
                      Glosario
                    </Link>
                    <Link to="/contact" className="list-group-item list-group-item-action">
                      <i className="fa-solid fa-envelope me-2 text-muted"></i>
                      Contactar Soporte
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Contenido principal */}
            <div className="col-lg-9">
              {filteredFAQs.length === 0 ? (
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center py-5">
                    <i className="fa-solid fa-search fa-4x text-muted mb-3"></i>
                    <h5>No se encontraron resultados</h5>
                    <p className="text-muted">
                      No hay preguntas que coincidan con tu búsqueda.
                      {searchQuery && (
                        <> Intenta con otras palabras clave.</>
                      )}
                    </p>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setSearchQuery('');
                        setActiveCategory('all');
                      }}
                    >
                      Ver todas las preguntas
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Contador de resultados */}
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <p className="text-muted mb-0">
                      Mostrando {filteredFAQs.length} de {faqItems.length} preguntas
                    </p>
                  </div>

                  {/* FAQs agrupadas por categoría */}
                  {activeCategory === 'all' ? (
                    Object.entries(groupedFAQs).map(([category, items]) => (
                      <div key={category} className="mb-4">
                        <h5 className="mb-3">
                          <i className="fa-solid fa-folder-open me-2 text-primary"></i>
                          {category}
                        </h5>
                        <div className="accordion" id={`accordion-${category.replace(/\s/g, '')}`}>
                          {items.map((faq) => (
                            <div className="accordion-item border-0 shadow-sm mb-2 rounded" key={faq.id}>
                              <h2 className="accordion-header">
                                <button
                                  className={`accordion-button ${!expandedItems.has(faq.id) ? 'collapsed' : ''}`}
                                  type="button"
                                  onClick={() => toggleItem(faq.id)}
                                >
                                  <i className={`fa-solid fa-question-circle me-3 ${expandedItems.has(faq.id) ? 'text-primary' : 'text-muted'}`}></i>
                                  {faq.question}
                                </button>
                              </h2>
                              <div className={`accordion-collapse collapse ${expandedItems.has(faq.id) ? 'show' : ''}`}>
                                <div className="accordion-body bg-light">
                                  <p className="mb-0">{faq.answer}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="accordion" id="accordion-filtered">
                      {filteredFAQs.map((faq) => (
                        <div className="accordion-item border-0 shadow-sm mb-2 rounded" key={faq.id}>
                          <h2 className="accordion-header">
                            <button
                              className={`accordion-button ${!expandedItems.has(faq.id) ? 'collapsed' : ''}`}
                              type="button"
                              onClick={() => toggleItem(faq.id)}
                            >
                              <i className={`fa-solid fa-question-circle me-3 ${expandedItems.has(faq.id) ? 'text-primary' : 'text-muted'}`}></i>
                              {faq.question}
                            </button>
                          </h2>
                          <div className={`accordion-collapse collapse ${expandedItems.has(faq.id) ? 'show' : ''}`}>
                            <div className="accordion-body bg-light">
                              <p className="mb-0">{faq.answer}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Banner de contacto */}
              <div className="card border-0 bg-primary text-white mt-4">
                <div className="card-body p-4">
                  <div className="row align-items-center">
                    <div className="col-lg-8 mb-3 mb-lg-0">
                      <h5 className="mb-1">¿No encontraste tu respuesta?</h5>
                      <p className="mb-0 text-white-50">
                        Nuestro equipo de soporte está disponible para ayudarte
                      </p>
                    </div>
                    <div className="col-lg-4 text-lg-end">
                      <Link to="/contact" className="btn btn-light">
                        <i className="fa-solid fa-envelope me-2"></i>
                        Contactar Soporte
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />

      <style>{`
        .accordion-button:not(.collapsed) {
          background-color: #f8f9fa;
          color: #0d6efd;
        }
        .accordion-button:focus {
          box-shadow: none;
          border-color: #dee2e6;
        }
        .accordion-item {
          overflow: hidden;
        }
      `}</style>
    </>
  );
}
